import { StoreGradeRecord } from "./store-grade.model";

/**
 * Adapts one stored grade record into the inputs the analytics performance panel already
 * expects.
 *
 * The panel is the shared full breakdown surface. It renders in three places: docked in the
 * store spotlight panel, in the expanded spotlight view, and on the asset review page at
 * /detail/asset-review/:uuid. Previously each host assembled these inputs from five separate
 * live calls, which is why the grade could differ between them.
 *
 * Feeding all three from a single record is what keeps them consistent: the breakdown always
 * shows the same calculation the card is showing, including when an older record is selected
 * from history.
 *
 * Field names below intentionally match the panel's existing template, including the
 * misspelled `networkStabillity`. Renaming that is a separate change.
 */
export interface AnalyticsPanelInputs {
  storeData: any;
  storePerformanceData: any;
  realEstateQualityData: any;
  bannerStrengthData: any;
  competitiveImpactData: any;
  gradeBreakdownData: any;
}

/** Empty inputs, so the panel falls back to its own N/A handling rather than erroring. */
function emptyInputs(overallGrade?: string): AnalyticsPanelInputs {
  return {
    storeData: { overallGrade: overallGrade || "N/A" },
    storePerformanceData: null,
    realEstateQualityData: {},
    bannerStrengthData: {},
    competitiveImpactData: {},
    gradeBreakdownData: null
  };
}

export function toAnalyticsPanelInputs(record: StoreGradeRecord | null | undefined): AnalyticsPanelInputs {
  if (!record) {
    return emptyInputs();
  }

  // Records saved before the detail columns existed carry only the overall grade. Show that
  // much and leave the rest empty instead of rendering zeros as if they were real values.
  if (!record.hasDetail) {
    return emptyInputs(record.overall?.grade);
  }

  const totalSales = record.totalSalesPerformance;
  const perSqft = record.salesPerSqft;

  return {
    storeData: { overallGrade: record.overall?.grade || "N/A" },

    storePerformanceData: {
      salesTotalPerformance: {
        salesTotalGrade: totalSales?.grade,
        salesIndexScore: totalSales?.indexScore,
        annualSalesTotal: totalSales?.annualSalesTotal,
        vsTradeAreaSalesAverage: totalSales?.vsTradeAreaAverage,
        vsBannerMarketSalesAverage: totalSales?.vsBannerMarketAverage,
        vsMarketSalesAverage: totalSales?.vsMarketAverage
      },
      salesPerSqftPerformance: {
        salesPerSqftGrade: perSqft?.grade,
        salesPerSqftIndexScore: perSqft?.indexScore,
        annualSalesPerSqft: perSqft?.annualSalesPerSqft,
        vsTradeAreaSalesPerSqftAverage: perSqft?.vsTradeAreaAverage,
        vsBannerMarketSalesPerSqftAverage: perSqft?.vsBannerMarketAverage,
        vsMarketSalesPerSqftAverage: perSqft?.vsMarketAverage
      },
      driveTimeMinutes: record.driveTimeMinutes
    },

    realEstateQualityData: {
      grade: record.realEstateQuality?.grade,
      realEstateScore: record.realEstateQuality?.score,
      accessibility: record.realEstateQuality?.accessibility,
      parking: record.realEstateQuality?.parking,
      visibility: record.realEstateQuality?.visibility,
      trafficFlow: record.realEstateQuality?.trafficFlow,
      scCondition: record.realEstateQuality?.scCondition
    },

    bannerStrengthData: {
      grade: record.bannerStrength?.grade,
      bannerStrengthScore: record.bannerStrength?.score,
      creditRating: record.bannerStrength?.creditRating,
      networkStabillity: record.bannerStrength?.networkStability,
      marketPresence: record.bannerStrength?.marketPresence
    },

    competitiveImpactData: {
      competitiveImpactGrade: record.competitiveImpact?.grade,
      competitiveImpactScore: record.competitiveImpact?.score,
      driveTimeRadiusMinutes: record.competitiveImpact?.driveTimeRadiusMinutes,
      competitiveImpactList: (record.competitiveImpact?.competitors || []).map(competitor => ({
        storeName: competitor.storeName,
        storeAddress: competitor.storeAddress,
        distance: competitor.distance
      }))
    },

    // Weightings as they were when this record was calculated, not today's values.
    // The panel's GradeBreakdownData uses shorter keys than the API, so map them across
    // rather than passing componentWeighting through unchanged.
    gradeBreakdownData: record.componentWeighting
      ? {
          sales: record.componentWeighting.totalSales,
          spsf: record.componentWeighting.salesPerSqft,
          realEstate: record.componentWeighting.realEstateQuality,
          banner: record.componentWeighting.bannerStrength,
          competitiveImpact: record.componentWeighting.competitiveImpact
        }
      : null
  };
}
